// src/utils/contentProcessor.js
// Content processing - keeps inline citations, strips think tags

import { extractCleanContent, validateParsedContent } from './reasoningParser';

const DEBUG = process.env.NODE_ENV === 'development';

// Known publication names by domain
const DOMAIN_MAP = {
  'reuters.com': 'Reuters',
  'bloomberg.com': 'Bloomberg',
  'apnews.com': 'Associated Press',
  'wsj.com': 'Wall Street Journal',
  'nytimes.com': 'New York Times',
  'cnbc.com': 'CNBC',
  'ft.com': 'Financial Times',
  'cnn.com': 'CNN',
  'bbc.com': 'BBC',
  'bbc.co.uk': 'BBC',
  'washingtonpost.com': 'Washington Post',
  'theguardian.com': 'The Guardian',
  'economist.com': 'The Economist',
  'politico.com': 'Politico',
  'axios.com': 'Axios',
  'thehill.com': 'The Hill',
  'npr.org': 'NPR',
  'cbc.ca': 'CBC News',
  'theglobeandmail.com': 'Globe and Mail',
  'youtube.com': 'YouTube',
  'wikipedia.org': 'Wikipedia',
  'whitehouse.gov': 'White House',
  'state.gov': 'State Department'
};

/**
 * Process content text - extracts clean content and converts inline citations to clickable links
 * @param {string} content - The raw content
 * @param {Object} citations - Citation map { "1": "url1", "2": "url2", ... }
 */
export function processCitations(content, citations = {}) {
  if (!content) return '';

  // Extract clean content from reasoning model output (handles AI refusals, strips <think> tags)
  const cleanContent = extractCleanContent(content);

  if (DEBUG) {
    const validation = validateParsedContent(cleanContent);
    if (!validation.isValid) {
      console.warn('Content parsing issues:', validation.issues);
    }
  }

  try {
    let processedContent = cleanContent;

    // Helper to get domain from URL
    const getDomain = (url) => {
      try {
        return new URL(url).hostname.replace(/^www\./, '');
      } catch {
        return 'source';
      }
    };

    // Helper to get nice publication name from domain
    const getPublicationName = (domain) => {
      const clean = domain.replace(/^www\./, '');
      for (const [key, name] of Object.entries(DOMAIN_MAP)) {
        if (clean === key || clean.endsWith('.' + key)) return name;
      }
      return clean.split('.')[0].replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    };

    // Helper to generate superscript citation HTML with rich tooltip
    const createSuperscriptCitation = (url, num) => {
      const domain = getDomain(url);
      const name = getPublicationName(domain);
      const faviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
      return `<a class="inline-citation" href="${url}" target="_blank" rel="noopener noreferrer" data-source-url="${url}" data-source-name="${name}" data-favicon-url="${faviconUrl}" data-citation-num="${num}"><sup>${num}</sup></a>`;
    };

    // Pattern: [n][URL] format - convert to clickable citation
    processedContent = processedContent.replace(
      /\[(\d+)\]\[(https?:\/\/[^\]]+)\]/g,
      (match, num, url) => createSuperscriptCitation(url, num)
    );

    // Pattern: [n: URL] - convert to clickable citation with domain
    processedContent = processedContent.replace(
      /\[(\d+)\s*:\s*(https?:\/\/[^\]]+)\]/g,
      (match, num, url) => createSuperscriptCitation(url, num)
    );

    // Pattern: plain [n] citations - look up URL from citations map
    processedContent = processedContent.replace(
      /\[(\d+)\]/g,
      (match, num) => {
        const url = citations[num] || citations[String(num)];

        if (url) {
          return createSuperscriptCitation(url, num);
        }
        // No URL found - show as non-clickable superscript
        return `<span class="inline-citation inline-citation--no-source"><sup>${num}</sup></span>`;
      }
    );

    // Clean up any double spaces or awkward punctuation
    processedContent = processedContent
      .replace(/[ \t]+\./g, '.')
      .replace(/[ \t]+,/g, ',')
      .replace(/[ \t]{2,}/g, ' ')
      .trim();

    return processedContent;
  } catch (error) {
    if (DEBUG) console.error('Error processing content:', error);
    return cleanContent;
  }
}
